import React from 'react';
import { LayoutDashboard, ArrowLeftRight, PieChart, TrendingUp, Target, CreditCard, StickyNote, Settings, ChevronLeft, ChevronRight, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { PossibleLogo } from '../ui/PossibleLogo';
export type DashboardView = 'overview' | 'transactions' | 'budget' | 'investments' | 'goals' | 'credit' | 'notepad' | 'settings';
interface NavItem {
  id: DashboardView;
  label: string;
  icon: React.ElementType;
}
const navItems: NavItem[] = [{
  id: 'overview',
  label: 'Overview',
  icon: LayoutDashboard
}, {
  id: 'transactions',
  label: 'Transactions',
  icon: ArrowLeftRight
}, {
  id: 'budget',
  label: 'Budget',
  icon: PieChart
}, {
  id: 'investments',
  label: 'Investments',
  icon: TrendingUp
}, {
  id: 'goals',
  label: 'Goals',
  icon: Target
}, {
  id: 'credit',
  label: 'Credit & Loans',
  icon: CreditCard
}, {
  id: 'notepad',
  label: 'AI Notepad',
  icon: StickyNote
}, {
  id: 'settings',
  label: 'Settings',
  icon: Settings
}];
interface SidebarProps {
  activeView: DashboardView;
  onViewChange: (view: DashboardView) => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
}
export function Sidebar({
  activeView,
  onViewChange,
  collapsed,
  onToggleCollapse
}: SidebarProps) {
  const navigate = useNavigate();
  const {
    isDark
  } = useTheme();
  return <aside className={`fixed left-0 top-0 h-full z-30 flex flex-col transition-all duration-300 ease-in-out border-r
        ${isDark ? 'bg-dark-sidebar border-dark-border' : 'bg-white border-light-border'}
        ${collapsed ? 'w-16' : 'w-60'}`} data-id="element-199">
      {/* Logo */}
      <div className={`flex items-center h-16 px-4 border-b shrink-0
          ${isDark ? 'border-dark-border' : 'border-light-border'}
          ${collapsed ? 'justify-center' : 'gap-3'}`} data-id="element-200">
        <PossibleLogo height={collapsed ? 14 : 16} variant={isDark ? 'dark-mode' : 'color'} iconOnly={collapsed} data-id="element-201" />
      </div>

      {/* Nav */}
      <nav className="flex-1 py-4 px-2 space-y-0.5 overflow-y-auto" data-id="element-202">
        {navItems.map(item => {
        const Icon = item.icon;
        const isActive = activeView === item.id;
        return <button key={item.id} onClick={() => onViewChange(item.id)} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-150 group
                ${isActive ? isDark ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30' : 'bg-primary-50 text-primary-600 border border-primary-100' : isDark ? 'text-gray-400 hover:bg-white/5 hover:text-gray-200' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-800'}
                ${collapsed ? 'justify-center' : ''}`} title={collapsed ? item.label : undefined} data-id="element-203">
              <Icon size={18} className={`shrink-0 ${isActive ? isDark ? 'text-primary-400' : 'text-primary-600' : ''}`} data-id="element-204" />
              {!collapsed && <span className="text-sm font-medium" data-id="element-205">{item.label}</span>}
              {isActive && !collapsed && <div className={`ml-auto w-1.5 h-1.5 rounded-full ${isDark ? 'bg-primary-400' : 'bg-primary-500'}`} data-id="element-206" />}
            </button>;
      })}
      </nav>

      {/* User section */}
      <div className={`shrink-0 p-3 border-t ${isDark ? 'border-dark-border' : 'border-light-border'}`} data-id="element-207">
        {!collapsed ? <div className={`flex items-center gap-3 px-2 py-2 rounded-lg cursor-pointer group
            ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`} data-id="element-208">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-accent-400 to-accent-600 flex items-center justify-center text-white text-sm font-bold shrink-0" data-id="element-209">
              A
            </div>
            <div className="flex-1 min-w-0" data-id="element-210">
              <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-211">
                Alex Johnson
              </p>
              <p className="text-xs text-gray-500 truncate" data-id="element-212">alex@example.com</p>
            </div>
            <button onClick={() => navigate('/')} className={`opacity-0 group-hover:opacity-100 p-1 rounded-lg transition-all text-gray-400
                ${isDark ? 'hover:bg-white/10 hover:text-white' : 'hover:bg-gray-100 hover:text-gray-700'}`} title="Log out" data-id="element-213">
              <LogOut size={14} data-id="element-214" />
            </button>
          </div> : <div className="flex justify-center" data-id="element-215">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-accent-400 to-accent-600 flex items-center justify-center text-white text-sm font-bold cursor-pointer" data-id="element-216">
              A
            </div>
          </div>}
      </div>

      {/* Collapse toggle */}
      <button onClick={onToggleCollapse} className={`absolute -right-3 top-20 w-6 h-6 rounded-full border flex items-center justify-center transition-colors shadow-md
          ${isDark ? 'bg-dark-border border-dark-border text-gray-400 hover:text-white hover:bg-primary-500' : 'bg-white border-light-border text-gray-400 hover:text-primary-600 hover:border-primary-200'}`} data-id="element-217">
        {collapsed ? <ChevronRight size={12} data-id="element-218" /> : <ChevronLeft size={12} data-id="element-219" />}
      </button>
    </aside>;
}